import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '../../ui/card';
import { Button } from '../../ui/button';
import Input from '../../ui/input';
import { Alert, AlertDescription } from '../../ui/alert';
import { verifyTwoFactor, refreshUserData } from '../../../services/userService';
import { SafeDatabaseUser } from '../../../types/types';

interface TwoFactorVerifyProps {
  username: string;
  onSuccess: (userData: SafeDatabaseUser) => void;
  onCancel: () => void;
  mode?: 'login' | 'signup';
  onSignupVerify?: (code: string) => Promise<SafeDatabaseUser | undefined>;
}

/**
 * Component for verifying two-factor authentication during login or signup
 */
const TwoFactorVerify: React.FC<TwoFactorVerifyProps> = ({
  username,
  onSuccess,
  onCancel,
  mode = 'login',
  onSignupVerify,
}) => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleVerificationCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6));
  };

  const handleVerify = async () => {
    if (!verificationCode) {
      setError('Please enter the verification code');
      return;
    }

    try {
      setIsLoading(true);

      // Different verification flow for signup vs login
      if (mode === 'signup' && onSignupVerify) {
        const userData = await onSignupVerify(verificationCode);
        if (userData) {
          onSuccess(userData);
        }
      } else {
        // Login verification flow
        const userData = await verifyTwoFactor(username, verificationCode);

        // Refresh user data to ensure we have the latest isAdmin status
        try {
          const refreshedUserData = await refreshUserData(username);
          onSuccess(refreshedUserData);
        } catch (refreshError) {
          // If refresh fails, still use the original userData
          onSuccess(userData);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify code');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
      <Card className='w-[400px] max-w-[90vw]'>
        <CardHeader>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            A verification code has been sent to your email. Please enter it below to{' '}
            {mode === 'signup' ? 'complete your signup' : 'complete your login'}.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            <div className='space-y-2'>
              <label htmlFor='verificationCode' className='text-sm font-medium'>
                Verification Code
              </label>
              <Input
                id='verificationCode'
                type='text'
                value={verificationCode}
                onChange={handleVerificationCodeChange}
                placeholder='Enter 6-digit code'
                maxLength={6}
                className='text-center text-xl tracking-widest'
              />
            </div>
          </div>
          {error && (
            <Alert variant='destructive' className='mt-4'>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </CardContent>
        <CardFooter className='flex justify-between'>
          <Button variant='outline' onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleVerify} disabled={isLoading || verificationCode.length !== 6}>
            {isLoading ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : 'Verify'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default TwoFactorVerify;
